import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import {
  Description,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from './common/Catalyst/fieldset';
import { Input } from './common/Catalyst/input';
import { Button } from './common/Catalyst/button';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { apiErrorMessage } from './utils/apiErrors';
import { bankService } from './clients/ApiClients';
import { PayableReturn, PayableSettleReturn } from './clients/rust-api-client';
import { formatCurrency } from './utils/bankHelpers';
import {
  payableStatusColor,
  payableStatusLabel,
  sumAmounts,
} from './utils/payableHelpers';
import { getUserName } from './utils/TokenUtils';

interface PayableSettleDialogProps {
  open: boolean;
  /** The pending items to settle together. */
  items: PayableReturn[];
  connectionId: string;
  onClose: () => void;
  /** The items as the API returned them after the settle — each one
   *  settled, or back to pending with the bank's reason. */
  onSettled: (after: PayableReturn[]) => void;
}

/**
 * Settle a batch with a one-time RSA code. **This is the call that moves
 * money.** The code is sent to the bank once and never stored; the RSA
 * token locks after three wrong codes, and only the bank unlocks it —
 * so the dialog says so, and a refusal is shown with the bank's words
 * rather than retried.
 */
const PayableSettleDialog: React.FC<PayableSettleDialogProps> = ({
  open,
  items,
  connectionId,
  onClose,
  onSettled,
}) => {
  const [username, setUsername] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PayableSettleReturn | null>(null);

  useEffect(() => {
    if (open) {
      // The RSA username is the person's own email, observed live.
      setUsername(getUserName() ?? '');
      setCode('');
      setError(null);
      setResult(null);
    }
  }, [open]);

  // Once settled, the panel clears its selection under us; the title
  // and total then come from what was sent, not from the empty set.
  const shown = result ? result.items : items;
  const total = useMemo(() => sumAmounts(shown), [shown]);
  const canSubmit =
    items.length > 0 &&
    !submitting &&
    username.trim().length > 0 &&
    code.trim().length >= 4;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      setError(null);
      const resp = await bankService.settleConnectionPayables({
        connectionId,
        payableSettleRequest: {
          payable_ids: items.map((i) => i.id),
          rsa_username: username.trim(),
          rsa_code: code.trim(),
        },
      });
      setCode('');
      setResult(resp.data);
      onSettled(resp.data.items);
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't settle."));
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? () => undefined : onClose}>
      <DialogTitle>
        {result ? 'Settled' : 'Settle'} {shown.length}{' '}
        {shown.length === 1 ? 'item' : 'items'} · {formatCurrency(total)}
      </DialogTitle>
      <DialogDescription>
        This sends the batch to the bank. The money moves on the bank’s next
        processing day. Enter the code from your RSA token; it is used once and
        never stored.
      </DialogDescription>
      <DialogBody>
        {error && (
          <ErrorAlert
            className="mb-4"
            title="Not settled"
            onDismiss={() => setError(null)}
          >
            {error}
          </ErrorAlert>
        )}

        {result ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Badge
                color={
                  result.outcome === 'sent'
                    ? result.not_settled === 0
                      ? 'green'
                      : 'amber'
                    : 'red'
                }
              >
                {result.outcome === 'sent'
                  ? `${result.settled} settled, ${result.not_settled} not`
                  : result.outcome === 'refused'
                    ? 'Refused by the bank'
                    : 'Could not confirm'}
              </Badge>
            </div>
            {result.bank_message && (
              <Text className="text-sm">{result.bank_message}</Text>
            )}
            {result.outcome === 'refused' && (
              <Text className="text-sm">
                Nothing was settled; every item is still waiting. Check the code
                and try again —{' '}
                <strong>the RSA token locks after three wrong codes</strong>,
                and only Columbia Bank can unlock it.
              </Text>
            )}
            {result.outcome === 'error' && (
              <Text className="text-sm">
                The bank did not answer clearly. Each item was read back and is
                shown as the bank holds it; do not re-enter a code until the
                table below agrees with Online Banking.
              </Text>
            )}
            <ul className="flex flex-col gap-1">
              {result.items.map((p) => (
                <li key={p.id} className="flex items-center gap-2 text-sm">
                  <Badge color={payableStatusColor(p.status)}>
                    {payableStatusLabel(p)}
                  </Badge>
                  <span>
                    {p.memo ?? p.id} · {formatCurrency(Number(p.amount))}
                  </span>
                  {p.status !== 'settled' && p.error_message && (
                    <Text className="text-xs text-danger">
                      {p.error_message}
                    </Text>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <Fieldset>
            <FieldGroup>
              <ul className="max-h-40 overflow-y-auto text-sm">
                {items.map((p) => (
                  <li key={p.id} className="flex justify-between gap-4">
                    <span className="truncate">
                      {p.memo ?? p.id} · acct {p.account_number}
                    </span>
                    <span className="tabular-nums">
                      {formatCurrency(Number(p.amount))}
                    </span>
                  </li>
                ))}
              </ul>
              <Field>
                <Label>RSA username</Label>
                <Input
                  name="rsa_username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                />
                <Description>
                  The username on your RSA token — usually your email.
                </Description>
              </Field>
              <Field>
                <Label>RSA code</Label>
                <Input
                  name="rsa_code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  placeholder="The code showing on your token now"
                />
                <Description>
                  One use per code. Three wrong codes lock the token, and only
                  Columbia Bank can unlock it.
                </Description>
              </Field>
            </FieldGroup>
          </Fieldset>
        )}
      </DialogBody>
      <DialogActions>
        {result ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button plain onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button color="sky" onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? 'Settling…' : `Settle ${formatCurrency(total)}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PayableSettleDialog;
